import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaCog, FaRedo, FaCheck, FaTimes, FaPlay } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';

// Helper functions to convert between ISO strings and datetime-local format
const toDatetimeLocal = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  // Get local time values
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const fromDatetimeLocal = (datetimeLocalString) => {
  if (!datetimeLocalString) return null;
  // Create a date from the local datetime string and convert to ISO
  const date = new Date(datetimeLocalString);
  return date.toISOString();
};

export default function CountdownComponent({ component, onUpdate }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || {};
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [overtime, setOvertime] = useState(false);
  const [flash, setFlash] = useState(false);
  const requestRef = useRef();
  const previousTimeRef = useRef();

  // Check if timer is active (has start date)
  const isActive = !!data.start_date;

  // Initialize on creation
  useEffect(() => {
    if (!data.created_at) {
      onUpdate({
        ...component,
        data: {
          ...data,
          created_at: new Date().toISOString()
        }
      });
    }
  }, []);

  // Flash animation when reaching zero
  useEffect(() => {
    if (timeLeft <= 0 && isActive && !overtime) {
      setFlash(true);
      const timer = setTimeout(() => setFlash(false), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft > 0) {
      setFlash(false);
    }
  }, [timeLeft, isActive, overtime]);

  // Calculate time left
  const calculateTimeLeft = () => {
    if (!isActive) {
      setTimeLeft(0);
      return;
    }

    const startDate = new Date(data.start_date);
    let endDate;
    
    if (data.mode === 'date') {
      endDate = new Date(data.end_date);
    } else {
      endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (data.duration?.days || 0));
      endDate.setHours(endDate.getHours() + (data.duration?.hours || 0));
      endDate.setMinutes(endDate.getMinutes() + (data.duration?.minutes || 0));
      endDate.setSeconds(endDate.getSeconds() + (data.duration?.seconds || 0));
    }

    const now = new Date();
    let diff = endDate - now;
    
    if (diff <= 0 && data.on_complete === 'continue') {
      setOvertime(true);
      diff = Math.abs(diff);
    } else if (diff <= 0) {
      diff = 0;
    }

    setTimeLeft(diff);
  };

  // Animation loop
  const animate = (time) => {
    if (previousTimeRef.current !== undefined) {
      calculateTimeLeft();
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [data, isActive]);

  // Format time
  const formatTime = () => {
    if (!isActive) return '00:00:00';
    if (timeLeft <= 0 && !overtime) return '00:00:00';
    
    const seconds = Math.floor((timeLeft / 1000) % 60);
    const minutes = Math.floor((timeLeft / 1000 / 60) % 60);
    const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    if (hours > 0) return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    if (minutes > 0) return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    return `${seconds}`;
  };

  // Format time for larger sizes (always show hours:minutes:seconds)
  const formatTimeLarge = () => {
    if (!isActive) return '00:00:00';
    if (timeLeft <= 0 && !overtime) return '00:00:00';
    
    const seconds = Math.floor((timeLeft / 1000) % 60);
    const minutes = Math.floor((timeLeft / 1000 / 60) % 60);
    const hours = Math.floor((timeLeft / (1000 * 60 * 60)) % 24);
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));

    if (days > 0) return `${days} day${days !== 1 ? 's' : ''}`;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format target time for inactive state
  const formatTargetTime = () => {
    if (data.mode === 'date') {
      const endDate = new Date(data.end_date);
      return endDate.toLocaleDateString() + ' ' + endDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    } else {
      const days = data.duration?.days || 0;
      const hours = data.duration?.hours || 0;
      const minutes = data.duration?.minutes || 0;
      const seconds = data.duration?.seconds || 0;
      
      const parts = [];
      if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
      if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
      if (minutes > 0) parts.push(`${minutes} min${minutes !== 1 ? 's' : ''}`);
      if (seconds > 0) parts.push(`${seconds} sec${seconds !== 1 ? 's' : ''}`);
      
      return parts.join(', ') || '0 seconds';
    }
  };

  // Calculate progress percentage (counts down now)
  const calculateProgress = () => {
    if (!isActive) return 0;

    const startDate = new Date(data.start_date);
    let endDate;
    
    if (data.mode === 'date') {
      endDate = new Date(data.end_date);
    } else {
      endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + (data.duration?.days || 0));
      endDate.setHours(endDate.getHours() + (data.duration?.hours || 0));
      endDate.setMinutes(endDate.getMinutes() + (data.duration?.minutes || 0));
      endDate.setSeconds(endDate.getSeconds() + (data.duration?.seconds || 0));
    }

    const total = endDate - startDate;
    const elapsed = overtime ? timeLeft : total - timeLeft;
    return Math.min(100, Math.max(0, 100 - (elapsed / total) * 100));
  };

  // Reset timer (remove start date)
  const resetTimer = () => {
    onUpdate({
      ...component,
      data: {
        ...data,
        start_date: null
      }
    });
    setOvertime(false);
  };

  // Start the countdown
  const startCountdown = () => {
    onUpdate({
      ...component,
      data: {
        ...data,
        start_date: new Date().toISOString()
      }
    });
  };

  // Update settings
  const updateSettings = (newData) => {
    onUpdate({
      ...component,
      data: {
        ...data,
        ...newData
      }
    });
  };

  return (
    <div className="countdown-component relative p-4 h-full">
      {/* Only show toggle button in non-fullscreen mode */}
      {!isFullscreen && (
        <button 
          className="absolute top-3 right-3 p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] text-lg transition-colors z-10"
          onClick={() => setSettingsOpen(!settingsOpen)}
        >
          <FaCog />
        </button>
      )}

      {/* Settings Modal for non-fullscreen mode */}
      {!isFullscreen && settingsOpen && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] animate-fade-in">
          <div className="bg-[var(--bg-primary)] p-6 rounded-lg max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto shadow-xl border border-[var(--border-color)] animate-scale-in">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-[var(--text-primary)]">Countdown Settings</h3>
              <button 
                className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                onClick={() => setSettingsOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            
            {/* Reset timer */}
            {isActive && (
              <div className="mb-4">
                <div className="p-2 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] mb-3">
                  <div className="text-xs text-[var(--text-secondary)] mb-1">Started</div>
                  <div className="text-sm text-[var(--text-primary)]">
                    {new Date(data.start_date).toLocaleDateString()} {new Date(data.start_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                </div>
                <button 
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
                  onClick={resetTimer}
                >
                  <FaRedo /> Reset Timer
                </button>
              </div>
            )}
            
            {/* Countdown target */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">Countdown Target</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-[var(--bg-primary)] transition-colors">
                  <input
                    type="radio"
                    checked={data.mode === 'date'}
                    onChange={() => updateSettings({ mode: 'date' })}
                    className="w-4 h-4"
                  />
                  <span className="text-[var(--text-primary)]">Specific date & time</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-[var(--bg-primary)] transition-colors">
                  <input
                    type="radio"
                    checked={data.mode === 'duration'}
                    onChange={() => updateSettings({ mode: 'duration' })}
                    className="w-4 h-4"
                  />
                  <span className="text-[var(--text-primary)]">Duration from start</span>
                </label>
              </div>
              
              {data.mode === 'date' ? (
                <div className="mt-3">
                  <input
                    type="datetime-local"
                    value={toDatetimeLocal(data.end_date)}
                    onChange={(e) => updateSettings({ end_date: fromDatetimeLocal(e.target.value) })}
                    className="w-full p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                  />
                </div>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Days</label>
                    <input
                      type="number"
                      min="0"
                      value={data.duration?.days || 0}
                      onChange={(e) => updateSettings({ duration: { ...data.duration, days: parseInt(e.target.value) || 0 } })}
                      className="w-full p-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Hours</label>
                    <input
                      type="number"
                      min="0"
                      value={data.duration?.hours || 0}
                      onChange={(e) => updateSettings({ duration: { ...data.duration, hours: parseInt(e.target.value) || 0 } })}
                      className="w-full p-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Minutes</label>
                    <input
                      type="number"
                      min="0"
                      value={data.duration?.minutes || 0}
                      onChange={(e) => updateSettings({ duration: { ...data.duration, minutes: parseInt(e.target.value) || 0 } })}
                      className="w-full p-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Seconds</label>
                    <input
                      type="number"
                      min="0"
                      value={data.duration?.seconds || 0}
                      onChange={(e) => updateSettings({ duration: { ...data.duration, seconds: parseInt(e.target.value) || 0 } })}
                      className="w-full p-2 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Completion behavior */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">When countdown ends</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-[var(--bg-primary)] transition-colors">
                  <input
                    type="radio"
                    checked={data.on_complete === 'stop'}
                    onChange={() => updateSettings({ on_complete: 'stop' })}
                    className="w-4 h-4"
                  />
                  <span className="text-[var(--text-primary)]">Stop at zero</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-[var(--bg-primary)] transition-colors">
                  <input
                    type="radio"
                    checked={data.on_complete === 'continue'}
                    onChange={() => updateSettings({ on_complete: 'continue' })}
                    className="w-4 h-4"
                  />
                  <span className="text-[var(--text-primary)]">Continue counting up</span>
                </label>
              </div>
            </div>
            
            <button
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
              onClick={() => setSettingsOpen(false)}
            >
              <FaCheck /> Done
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Fullscreen layout: side by side */}
      {isFullscreen ? (
        <div className="flex gap-8 h-full">
          {/* Countdown display - left side */}
          <div className="flex-1 flex flex-col items-center justify-center">
            {/* Donut chart */}
            <div className="relative w-80 h-80 mb-8">
              <svg 
                className="w-full h-full" 
                viewBox="0 0 200 200" 
                style={{ 
                  shapeRendering: 'geometricPrecision'
                }}
              >
                {/* Background circle */}
                <circle
                  cx="100"
                  cy="100"
                  r="90"
                  fill="none"
                  stroke="var(--border-color)"
                  strokeWidth="4"
                />
                {/* Progress circle - only shown when active */}
                {isActive && (
                  <circle
                    cx="100"
                    cy="100"
                    r="90"
                    fill="none"
                    stroke={
                      timeLeft <= 0 
                        ? (flash ? 'var(--primary)' : 'var(--danger)') 
                        : 'var(--primary)'
                    }
                    strokeWidth="8"
                    strokeDasharray="565.49"
                    strokeDashoffset={565.49 - (565.49 * calculateProgress()) / 100}
                    transform="rotate(-90 100 100)"
                    strokeLinecap="round"
                    className={timeLeft <= 0 ? 'animate-pulse' : ''}
                    style={{ 
                      transition: 'stroke-dashoffset 0.3s ease-out, stroke 0.3s ease-out'
                    }}
                  />
                )}
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                {!isActive ? (
                  <>
                    <span className="text-lg text-[var(--text-secondary)] mb-2">Countdown set for</span>
                    <span className="text-3xl font-bold text-[var(--text-primary)] text-center px-4">
                      {formatTargetTime()}
                    </span>
                  </>
                ) : (
                  <span className={`text-6xl font-bold transition-colors duration-300 ${
                    timeLeft <= 0 
                      ? (flash ? 'text-[var(--primary)]' : 'text-[var(--danger)] animate-pulse') 
                      : 'text-[var(--text-primary)]'
                  }`}>
                    {overtime ? '+' : ''}{formatTime()}
                  </span>
                )}
              </div>
            </div>

            {/* Additional progress bars for fullscreen when active */}
            {isActive && (
              <div className="w-full max-w-md space-y-4 mb-8">
                {/* Days progress */}
                {Math.floor(timeLeft / (1000 * 60 * 60 * 24)) > 0 && (
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm text-[var(--text-secondary)]">
                      <span>Days</span>
                      <span>{Math.floor(timeLeft / (1000 * 60 * 60 * 24))}</span>
                    </div>
                    <div className="w-full bg-[var(--border-color)] rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-500 ease-out transform ${
                          timeLeft <= 0 ? 'bg-[var(--danger)] animate-pulse scale-105' : 'bg-[var(--primary)] hover:scale-105'
                        }`}
                        style={{ 
                          width: `${(Math.floor(timeLeft / (1000 * 60 * 60 * 24)) % 365) / 365 * 100}%`,
                          transition: 'width 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55), transform 0.3s ease-out'
                        }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {/* Hours progress */}
                <div className="space-y-1">
                  <div className="flex justify-between text-sm text-[var(--text-secondary)]">
                    <span>Hours</span>
                    <span>{Math.floor((timeLeft / (1000 * 60 * 60)) % 24)}</span>
                  </div>
                  <div className="w-full bg-[var(--border-color)] rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ease-out transform ${
                        timeLeft <= 0 ? 'bg-[var(--danger)] animate-pulse scale-105' : 'bg-[var(--primary)] hover:scale-105'
                      }`}
                      style={{ 
                        width: `${(Math.floor((timeLeft / (1000 * 60 * 60)) % 24) / 24) * 100}%`,
                        transition: 'width 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55), transform 0.3s ease-out'
                      }}
                    ></div>
                  </div>
                </div>
                
                {/* Minutes progress */}
                <div className="space-y-1">
                  <div className="flex justify-between text-sm text-[var(--text-secondary)]">
                    <span>Minutes</span>
                    <span>{Math.floor((timeLeft / 1000 / 60) % 60)}</span>
                  </div>
                  <div className="w-full bg-[var(--border-color)] rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-500 ease-out transform ${
                        timeLeft <= 0 ? 'bg-[var(--danger)] animate-pulse scale-105' : 'bg-[var(--primary)] hover:scale-105'
                      }`}
                      style={{ 
                        width: `${(Math.floor((timeLeft / 1000 / 60) % 60) / 60) * 100}%`,
                        transition: 'width 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55), transform 0.3s ease-out'
                      }}
                    ></div>
                  </div>
                </div>
                
                {/* Seconds progress */}
                <div className="space-y-1">
                  <div className="flex justify-between text-sm text-[var(--text-secondary)]">
                    <span>Seconds</span>
                    <span>{Math.floor((timeLeft / 1000) % 60)}</span>
                  </div>
                  <div className="w-full bg-[var(--border-color)] rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ease-out transform ${
                        timeLeft <= 0 ? 'bg-[var(--danger)] animate-pulse scale-110' : 'bg-[var(--primary)] hover:scale-105'
                      }`}
                      style={{ 
                        width: `${(Math.floor((timeLeft / 1000) % 60) / 60) * 100}%`,
                        transition: 'width 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55), transform 0.2s ease-out'
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )}

            {/* Start button when inactive */}
            {!isActive && (
              <button
                className="flex items-center gap-3 px-8 py-4 text-lg bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity shadow-lg"
                onClick={startCountdown}
              >
                <FaPlay /> Start Countdown
              </button>
            )}
          </div>

          {/* Settings panel - right side */}
          <div className="w-80 bg-[var(--bg-secondary)] rounded-xl p-6 border border-[var(--border-color)]">
            <h3 className="text-xl font-semibold mb-6 text-[var(--text-primary)]">Settings</h3>
            
            {/* Reset timer */}
            {isActive && (
              <div className="mb-6">
                <div className="p-3 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)] mb-3">
                  <div className="text-xs text-[var(--text-secondary)] mb-1">Started</div>
                  <div className="text-sm text-[var(--text-primary)]">
                    {new Date(data.start_date).toLocaleDateString()} {new Date(data.start_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                  </div>
                </div>
                <button 
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity w-full justify-center"
                  onClick={resetTimer}
                >
                  <FaRedo /> Reset Timer
                </button>
              </div>
            )}
            
            {/* Countdown target */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-3">Countdown Target</label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    checked={data.mode === 'date'}
                    onChange={() => updateSettings({ mode: 'date' })}
                    className="w-4 h-4"
                  />
                  <span className="text-[var(--text-primary)]">Specific date & time</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    checked={data.mode === 'duration'}
                    onChange={() => updateSettings({ mode: 'duration' })}
                    className="w-4 h-4"
                  />
                  <span className="text-[var(--text-primary)]">Duration from start</span>
                </label>
              </div>
              
              {data.mode === 'date' ? (
                <div className="mt-4">
                  <input
                    type="datetime-local"
                    value={data.end_date ? new Date(data.end_date).toISOString().slice(0, 16) : ''}
                    onChange={(e) => updateSettings({ end_date: e.target.value })}
                    className="w-full p-3 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                  />
                </div>
              ) : (
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Days</label>
                    <input
                      type="number"
                      min="0"
                      value={data.duration?.days || 0}
                      onChange={(e) => updateSettings({ duration: { ...data.duration, days: parseInt(e.target.value) || 0 } })}
                      className="w-full p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Hours</label>
                    <input
                      type="number"
                      min="0"
                      value={data.duration?.hours || 0}
                      onChange={(e) => updateSettings({ duration: { ...data.duration, hours: parseInt(e.target.value) || 0 } })}
                      className="w-full p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Minutes</label>
                    <input
                      type="number"
                      min="0"
                      value={data.duration?.minutes || 0}
                      onChange={(e) => updateSettings({ duration: { ...data.duration, minutes: parseInt(e.target.value) || 0 } })}
                      className="w-full p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1">Seconds</label>
                    <input
                      type="number"
                      min="0"
                      value={data.duration?.seconds || 0}
                      onChange={(e) => updateSettings({ duration: { ...data.duration, seconds: parseInt(e.target.value) || 0 } })}
                      className="w-full p-2 border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:border-[var(--primary)] focus:outline-none transition-colors"
                    />
                  </div>
                </div>
              )}
            </div>
            
            {/* Completion behavior */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-3">When countdown ends</label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    checked={data.on_complete === 'stop'}
                    onChange={() => updateSettings({ on_complete: 'stop' })}
                    className="w-4 h-4"
                  />
                  <span className="text-[var(--text-primary)]">Stop at zero</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="radio"
                    checked={data.on_complete === 'continue'}
                    onChange={() => updateSettings({ on_complete: 'continue' })}
                    className="w-4 h-4"
                  />
                  <span className="text-[var(--text-primary)]">Continue counting up</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Non-fullscreen countdown view */
        <div className="countdown-view flex flex-col items-center justify-center h-full">
          {/* Donut chart */}
          <div className="relative w-48 h-48 mb-4">
            <svg className="w-full h-full" viewBox="0 0 100 100">
              {/* Background circle */}
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="var(--border-color)"
                strokeWidth="2"
              />
              {/* Progress circle - only shown when active */}
              {isActive && (
                <circle
                  cx="50"
                  cy="50"
                  r="45"
                  fill="none"
                  stroke={
                    timeLeft <= 0 
                      ? (flash ? 'var(--primary)' : 'var(--danger)') 
                      : 'var(--primary)'
                  }
                  strokeWidth="3"
                  strokeDasharray="283"
                  strokeDashoffset={283 - (283 * calculateProgress()) / 100}
                  transform="rotate(-90 50 50)"
                  strokeLinecap="round"
                  className={timeLeft <= 0 ? 'animate-pulse' : ''}
                />
              )}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {!isActive ? (
                <>
                  <span className="text-sm text-[var(--text-secondary)] mb-2">Countdown set for</span>
                  <span className="text-xl font-bold text-[var(--text-primary)] text-center px-4">
                    {formatTargetTime()}
                  </span>
                </>
              ) : (
                <span className={`text-3xl font-bold transition-colors duration-300 ${
                  timeLeft <= 0 
                    ? (flash ? 'text-[var(--primary)]' : 'text-[var(--danger)] animate-pulse') 
                    : 'text-[var(--text-primary)]'
                }`}>
                  {overtime ? '+' : ''}{(component.size === 'large' || component.size === 'extra-large') ? formatTimeLarge() : formatTime()}
                </span>
              )}
            </div>
          </div>

          {/* Start button when inactive */}
          {!isActive && (
            <button
              className="flex items-center gap-2 px-4 py-2 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 transition-opacity"
              onClick={startCountdown}
            >
              <FaPlay /> Start Countdown
            </button>
          )}
        </div>
      )}
    </div>
  );
}